import { redirect } from 'react-router-dom';
import { MyRouteObject, checkAuth } from './index';
import { lazy, Suspense } from 'react';
import LoadingSpinner from '@/components/LoadingSpinner';

// 懒加载用户端组件
const UserLayout = lazy(() => import('@/layouts/UserLayout'));
const ZenLayout = lazy(() => import('@/layouts/ZenLayout'));
const Home = lazy(() => import('@/pages/client/home'));
const ProblemList = lazy(() => import('@/pages/client/problem/List'));
const TrainingList = lazy(() => import('@/pages/client/problem/TrainingList'));
const TrainingDetail = lazy(() => import('@/pages/client/problem/TrainingDetail'));
const TrainingEdit = lazy(() => import('@/pages/client/problem/TrainingEdit'));
const ContestList = lazy(() => import('@/pages/client/contest/List'));
const ContestDetail = lazy(() => import('@/pages/client/contest/Detail'));
const ContestRank = lazy(() => import('@/pages/client/contest/Rank'));
const PaperList = lazy(() => import('@/pages/client/paper/List'));
const PaperExam = lazy(() => import('@/pages/client/paper/PaperExam'));
const ExamResult = lazy(() => import('@/pages/client/paper/ExamResult'));
const Profile = lazy(() => import('@/pages/account/Profile'));
const CompleteProfile = lazy(() => import('@/pages/account/CompleteProfile'));
const Mall = lazy(() => import('@/pages/client/mall'));
// 阅读：公开只读内容管理「已发布」文章（侧边菜单位于「商城」之后）
const ReadingList = lazy(() => import('@/pages/client/reading'));
const ReadingDetail = lazy(() => import('@/pages/client/reading/Detail'));
const GameCenter = lazy(() => import('@/pages/client/game'));
const TypingPracticeGame = lazy(() => import('@/pages/client/game/typingpractice'));
const TypingGame = lazy(() => import('@/pages/client/game/typing'));
const CertificationCenter = lazy(() => import('@/pages/client/certification'));
const Workspace = lazy(() => import('@/pages/client/workspace'));
// 网站守则与积分/成长规则（数值全部来自后端 /api/rules/，避免说明页与代码漂移）
const Rules = lazy(() => import('@/pages/client/rules'));
const Review = lazy(() => import('@/pages/share/review'));
// 用户端「我的提交」与提交详情（详情复用管理端共用组件，后端已做越权校验）
const MySubmissionList = lazy(() => import('@/pages/client/submission/List'));
const SubmissionDetail = lazy(() => import('@/pages/share/submission/Detail'));

// 用户端路由配置
export const userRoutes: MyRouteObject[] = [
  {
    path: '/',
    element: <UserLayout />,
    children: [
      {
        index: true,
        element: <Home />,
        breadcrumb: '首页',
      },
      {
        path: 'profile',
        element: <Profile />,
        breadcrumb: '个人中心',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      {
        path: 'profile/complete',
        element: <CompleteProfile />,
        breadcrumb: '完善个人资料',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      // 复习中心（docs/25 §W4）：历史得分题回炉重做
      {
        path: 'review',
        element: <Review />,
        breadcrumb: '复习中心',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      // 我的提交：首页"最近动向 → 全部提交"的落地页（own_only 只看本人）
      {
        path: 'submission',
        children: [
          {
            index: true,
            loader: () => redirect('/submission/list'),
          },
          {
            path: 'list',
            element: <MySubmissionList />,
            breadcrumb: '我的提交',
            loader: () => checkAuth() ? null : redirect('/login'),
          },
          {
            path: ':id',
            element: <SubmissionDetail />,
            breadcrumb: '提交详情',
            loader: () => checkAuth() ? null : redirect('/login'),
          },
        ],
      },
      // 训练：系统核心入口（用户端包装组件已内置 basePath="/training"，这里不再重复传）
      {
        path: 'training',
        element: <TrainingList />,
        breadcrumb: '训练列表',
      },
      {
        path: 'training/create',
        element: <TrainingEdit />,
        breadcrumb: '创建训练',
      },
      {
        path: 'training/edit/:id',
        element: <TrainingEdit />,
        breadcrumb: '编辑训练',
      },
      {
        path: 'training/:id',
        element: <TrainingDetail />,
        breadcrumb: '训练详情',
      },
      {
        path: 'problem',
        children: [
          {
            index: true,
            loader: () => redirect('/problem/list'),
          },
          {
            path: 'list',
            element: <ProblemList />,
            breadcrumb: '题目列表',
          },
        ],
      },
      {
        path: 'contest',
        children: [
          {
            index: true,
            loader: () => redirect('/contest/list'),
          },
          {
            path: 'list',
            element: <ContestList />,
            breadcrumb: '比赛列表',
          },
          {
            path: 'rank/:id',
            element: <ContestRank />,
            breadcrumb: '比赛排名',
          },
          {
            path: ':id',
            element: <ContestDetail />,
            breadcrumb: '比赛详情',
          },
        ],
      },
      {
        path: 'paper/list',
        element: <PaperList />,
        breadcrumb: '试卷列表',
      },
      {
        path: 'certification',
        element: <CertificationCenter />,
        breadcrumb: '试炼台',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      {
        path: 'mall',
        element: <Mall />,
        breadcrumb: '积分商城',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      // 守则与规则：说明成长/积分/签到口径，登录用户可见
      {
        path: 'rules',
        element: <Rules />,
        breadcrumb: '守则',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      // 阅读：公开访问，读取内容管理的已发布文章（含 B 站外链视频）
      {
        path: 'reading',
        element: <ReadingList />,
        breadcrumb: '阅读',
      },
      {
        path: 'reading/:id',
        element: <ReadingDetail />,
        breadcrumb: '文章详情',
      },
      // 游戏中心：内置游戏目录（doc/27 打字防线 / doc/29 打字练习）
      {
        path: 'games',
        element: <GameCenter />,
        breadcrumb: '游戏中心',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      {
        path: 'games/typing-practice',
        element: <TypingPracticeGame />,
        breadcrumb: '打字练习',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      {
        path: 'games/typing',
        element: <TypingGame />,
        breadcrumb: '打字防线',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
      {
        path: 'paper/result/:id',
        element: <ExamResult />,
        breadcrumb: '成绩查看',
        loader: () => checkAuth() ? null : redirect('/login'),
      },
    ],
  },
  // 禅意（代码工作台）：独立布局，不继承用户端模板；从菜单以新标签页打开
  {
    path: 'workspace',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载中..." />}>
        <ZenLayout />
      </Suspense>
    ),
    breadcrumb: '禅意',
    loader: () => checkAuth() ? null : redirect('/login'),
    children: [
      {
        index: true,
        element: <Workspace />,
        breadcrumb: '禅意',
      },
    ],
  },
  // 答题页面独立，不使用 UserLayout
  {
    path: 'client/paper/exam/:id',
    element: <PaperExam />,
    breadcrumb: '答题',
    loader: () => checkAuth() ? null : redirect('/login'),
  },
]; 